/**
 * UIPreviewPage — AgriBooks Design System Showcase
 * Route: /ui-preview (public, no auth required)
 *
 * Shows all 12 system components in action.
 * Each section has: Current (before) vs Proposed (after) comparison.
 * Approve each section before the phase is implemented.
 */
import { useState } from 'react';
import {
  ShoppingCart, Receipt, Users, LayoutDashboard, Building2,
  AlertTriangle, Package, TrendingUp, ArrowUpRight, ArrowDownRight,
  Lock, Search, ChevronRight, Truck, Wallet,
} from 'lucide-react';

import { Num }           from '../components/system/Num';
import { StatusBadge }   from '../components/system/StatusBadge';
import { StatCard }      from '../components/system/StatCard';
import { PillAvatar }    from '../components/system/PillAvatar';
import { AlertRow }      from '../components/system/AlertRow';
import { DataTable }     from '../components/system/DataTable';
import { SectionHeader } from '../components/system/SectionHeader';
import { ToolbarPills }  from '../components/system/ToolbarPills';
import { PageHeader }    from '../components/system/PageHeader';
import { EmptyState }    from '../components/system/EmptyState';
import { DonutChart }    from '../components/system/DonutChart';
import { MiniLineChart } from '../components/system/MiniLineChart';

const php = (n) =>
  '₱' + Number(n).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Layout helpers ──────────────────────────────────────────────────────────
function DemoCard({ label, tone = 'proposed', children }) {
  return (
    <div className={`rounded-lg overflow-hidden border ${tone === 'current' ? 'border-slate-200' : 'border-emerald-200/60'}`}>
      <div className={`px-4 py-2 border-b flex items-center justify-between text-[10px] font-semibold uppercase tracking-widest ${
        tone === 'current'
          ? 'bg-slate-50 border-slate-200 text-slate-500'
          : 'bg-emerald-50/60 border-emerald-100 text-emerald-700'
      }`}>
        <span>{label}</span>
        <span className="font-normal">{tone === 'current' ? 'LIVE' : 'NEW'}</span>
      </div>
      <div className="p-6 bg-[#F5F5F0] min-h-[160px] flex items-start">
        {children}
      </div>
    </div>
  );
}

function Row({ title, note, children }) {
  return (
    <section className="mb-10">
      <div className="mb-3">
        <h2 className="text-[15px] font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>{title}</h2>
        {note && <p className="text-[12px] text-slate-500 mt-0.5">{note}</p>}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">{children}</div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SECTION DATA
// ══════════════════════════════════════════════════════════════════════════

const TABLE_ROWS = [
  { id: 1, invoice: 'SI-MB-001234', customer: 'Juan Dela Cruz',  amount: 12340.50, status: 'paid'    },
  { id: 2, invoice: 'SI-MB-001235', customer: 'Maria Santos',    amount:  8750.00, status: 'partial' },
  { id: 3, invoice: 'SI-MB-001236', customer: 'Pedro Reyes',     amount:  4200.00, status: 'paid'    },
  { id: 4, invoice: 'SI-MB-001237', customer: 'Ana Lopez',       amount: 24500.75, status: 'pending' },
];

const TABLE_COLS = [
  { key: 'invoice',  label: 'Invoice' },
  { key: 'customer', label: 'Customer' },
  { key: 'amount',   label: 'Amount',  numeric: true, align: 'right' },
  { key: 'status',   label: 'Status',  align: 'center' },
];

const DONUT_INVENTORY = [
  { label: 'Healthy',   value: 62, color: '#16A34A' },
  { label: 'Low',       value: 18, color: '#D97706' },
  { label: 'Out',       value:  8, color: '#DC2626' },
  { label: 'Overstock', value: 12, color: '#2563EB' },
];

const TREND_POINTS = [18000, 22000, 19000, 28000, 24000, 31000, 29000, 35000];

// ══════════════════════════════════════════════════════════════════════════
// COMPONENT DEMOS
// ══════════════════════════════════════════════════════════════════════════

// 1. Num
function OldNum() {
  return (
    <div className="space-y-2 text-sm">
      <div className="flex justify-between text-slate-700"><span className="text-slate-500">Invoice</span><span>SI-MB-001234</span></div>
      <div className="flex justify-between text-slate-700"><span className="text-slate-500">Amount</span><span className="font-bold">₱1,234,567.89</span></div>
      <div className="flex justify-between text-slate-700"><span className="text-slate-500">Variance</span><span className="text-red-600">-₱150.00</span></div>
      <div className="flex justify-between text-slate-700"><span className="text-slate-500">Stock</span><span>128 bags</span></div>
    </div>
  );
}
function NewNum() {
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Invoice</span>
        <Num value="SI-MB-001234" />
      </div>
      <div className="flex justify-between items-center">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Amount</span>
        <Num value="₱1,234,567.89" tone="neutral" bold />
      </div>
      <div className="flex justify-between items-center">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Variance</span>
        <Num value="-₱150.00" tone="negative" bold />
      </div>
      <div className="flex justify-between items-center">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Stock</span>
        <Num value="128 bags" />
      </div>
    </div>
  );
}

// 2. StatusBadge
function OldBadges() {
  return (
    <div className="flex flex-wrap gap-2">
      {[['Open','bg-emerald-50 border border-emerald-200 text-emerald-700'],
        ['Closed','bg-slate-100 border border-slate-200 text-slate-600'],
        ['Short','bg-red-50 border border-red-200 text-red-700'],
        ['Over','bg-blue-50 border border-blue-200 text-blue-700'],
        ['Pending','bg-amber-50 border border-amber-200 text-amber-700'],
        ['Paid','bg-emerald-50 border border-emerald-200 text-emerald-700'],
      ].map(([l,c]) => <span key={l} className={`px-2 py-0.5 rounded text-[10px] font-semibold ${c}`}>{l}</span>)}
    </div>
  );
}
function NewBadges() {
  return (
    <div className="flex flex-wrap gap-2">
      {['open','closed','short','over','pending','paid','voided','partial','draft','approved'].map(v => (
        <StatusBadge key={v} variant={v} />
      ))}
    </div>
  );
}

// 3. StatCard
function OldStatCard() {
  return (
    <div className="bg-white rounded-lg border-[var(--border-default)] shadow-sm p-5 w-full max-w-xs">
      <div className="text-sm text-slate-500 mb-2">Today's Sales</div>
      <div className="text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope' }}>₱48,250.00</div>
      <div className="flex items-center gap-1 text-sm">
        <ArrowUpRight size={14} className="text-emerald-600" />
        <span className="text-emerald-600 font-medium">+12.4%</span>
        <span className="text-slate-400">vs yesterday</span>
      </div>
    </div>
  );
}
function NewStatCards() {
  return (
    <div className="flex flex-col gap-3 w-full">
      <div className="flex gap-3">
        <StatCard label="Today's Sales" value="₱48,250" delta="12.4%" deltaTone="positive"
          footer="vs ₱42,930 yesterday" icon={ShoppingCart} iconTone="success" />
        <StatCard label="Expenses" value="₱3,820" delta="8.1%" deltaTone="negative"
          footer="vs ₱3,532 yesterday" icon={Receipt} iconTone="warning" />
      </div>
      <div className="flex gap-3">
        <StatCard label="AR Outstanding" value="₱124,500" icon={Users} iconTone="danger" />
        <StatCard label="Cash in Safe" value="₱136,400" icon={Wallet} iconTone="info" />
      </div>
    </div>
  );
}

// 4. PillAvatar
function OldPillAvatar() {
  return (
    <div className="space-y-3">
      {[
        { icon: ShoppingCart, label: 'SI-MB-001234 — Sale', color: 'text-emerald-700' },
        { icon: Receipt,      label: 'EXP-001 — Fuel',      color: 'text-amber-700'   },
        { icon: Truck,        label: 'PO-0012 — Supplies',  color: 'text-blue-700'    },
      ].map(({ icon: Icon, label, color }) => (
        <div key={label} className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
            <Icon size={14} className={color} />
          </div>
          <span className="text-[13px] text-slate-700">{label}</span>
        </div>
      ))}
    </div>
  );
}
function NewPillAvatar() {
  return (
    <div className="space-y-3">
      {[
        { icon: ShoppingCart, label: 'SI-MB-001234 — Sale',   tone: 'success' },
        { icon: Receipt,      label: 'EXP-001 — Fuel',        tone: 'warning' },
        { icon: Truck,        label: 'PO-0012 — Supplies',    tone: 'info'    },
        { icon: Lock,         label: 'SH-001 — Shift closed', tone: 'neutral' },
      ].map(({ icon, label, tone }) => (
        <div key={label} className="flex items-center gap-3">
          <PillAvatar icon={icon} tone={tone} />
          <span className="text-[13px]" style={{ color: 'var(--text-primary)' }}>{label}</span>
        </div>
      ))}
    </div>
  );
}

// 5. AlertRow
function OldAlerts() {
  return (
    <div className="space-y-2">
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 flex items-center gap-2">
        <AlertTriangle size={15} className="text-amber-600 flex-shrink-0" />
        3 unclosed business days found.
      </div>
      <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800 flex items-center gap-2">
        <AlertTriangle size={15} className="text-red-600 flex-shrink-0" />
        Shift variance -₱150.00 detected.
      </div>
    </div>
  );
}
function NewAlerts() {
  return (
    <div className="space-y-2 w-full">
      <AlertRow severity="warning" icon={AlertTriangle} title="3 unclosed business days"
        detail="Daily close is required before making today's sales." action="Close now" />
      <AlertRow severity="danger" icon={Lock} title="Shift variance detected"
        detail="Cashier Ana Reyes is -₱150.00 short. Dual-signature required." action="Review" />
      <AlertRow severity="info" icon={TrendingUp} title="Sales up 12% this week"
        detail="Main Branch outperforming last 4-week average." />
      <AlertRow severity="success" icon={Package} title="All shifts balanced"
        detail="No variance across 3 cashiers today." />
    </div>
  );
}

// 6. DataTable
function OldDataTable() {
  return (
    <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
      <thead className="bg-slate-50 border-b border-slate-200">
        <tr>
          {['Invoice','Customer','Amount','Status'].map(h => (
            <th key={h} className="text-left px-4 py-3 font-semibold text-slate-700 text-xs">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {TABLE_ROWS.map((r,i) => (
          <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
            <td className="px-4 py-3 text-slate-700">{r.invoice}</td>
            <td className="px-4 py-3 text-slate-700">{r.customer}</td>
            <td className="px-4 py-3 text-right text-slate-700">{php(r.amount)}</td>
            <td className="px-4 py-3 text-center">
              <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${
                r.status === 'paid' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
                r.status === 'partial' ? 'bg-amber-50 border-amber-200 text-amber-700' :
                'bg-blue-50 border-blue-200 text-blue-700'}`}>{r.status}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
function NewDataTable() {
  return (
    <DataTable
      columns={TABLE_COLS}
      rows={TABLE_ROWS}
      renderCell={(row, col) => {
        if (col.key === 'amount') return <Num value={php(row.amount)} tone="neutral" />;
        if (col.key === 'status') return <StatusBadge variant={row.status} />;
        return row[col.key];
      }}
      footer={
        <div className="flex justify-between items-center text-[13px]">
          <span style={{ color: 'var(--text-secondary)' }}>4 invoices</span>
          <div className="flex items-center gap-1">
            <span style={{ color: 'var(--text-secondary)' }}>Total:</span>
            <Num value={php(TABLE_ROWS.reduce((s,r)=>s+r.amount,0))} bold />
          </div>
        </div>
      }
    />
  );
}

// 7. ToolbarPills
function OldTabs() {
  const [active, setActive] = useState('sale');
  return (
    <div className="inline-flex items-center bg-slate-100/80 rounded-xl p-1 shadow-inner ring-1 ring-slate-200/40">
      {[['sale','New Sale',ShoppingCart],['history','History',Receipt]].map(([v,l,Icon]) => (
        <button key={v} onClick={() => setActive(v)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
            active === v ? 'bg-white shadow-sm ring-1 ring-slate-200/60 text-[#1A4D2E]' : 'text-slate-500'
          }`}>
          <Icon size={13} />{l}
        </button>
      ))}
    </div>
  );
}
function NewTabs() {
  const [active, setActive] = useState('sale');
  return (
    <div className="flex flex-col gap-4">
      <ToolbarPills
        value={active}
        onChange={setActive}
        options={[
          { value: 'sale',    label: 'New Sale',  icon: ShoppingCart },
          { value: 'history', label: 'History',   icon: Receipt, badge: 3 },
          { value: 'drafts',  label: 'Drafts',    icon: Lock,    badge: 1 },
        ]}
      />
      <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
        Active: <strong>{active}</strong> — badge counts, left accent bar, 80ms transitions
      </p>
    </div>
  );
}

// 8. PageHeader
function OldPageHeader() {
  return (
    <div className="flex items-center justify-between pb-4 border-b border-slate-200">
      <div>
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 inline-block mb-2">
          3 unclosed days — please close before proceeding
        </div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Sales</h1>
      </div>
      <button className="bg-[#1A4D2E] text-white px-4 py-2 rounded-lg text-sm font-medium">Export</button>
    </div>
  );
}
function NewPageHeader() {
  return (
    <PageHeader
      title="Sales"
      breadcrumb="Transactions"
      warningBar="3 unclosed business days — daily close required before today's sale."
      actions={
        <button className="px-3 h-9 rounded-md text-[13px] font-semibold text-white"
          style={{ backgroundColor: 'var(--brand-primary)' }}>
          Export
        </button>
      }
    />
  );
}

// 9. SectionHeader
function OldSectionHeader() {
  return (
    <div className="flex items-center justify-between mb-4">
      <h2 className="font-bold text-slate-800 text-base">Sales Summary</h2>
      <button className="text-sm text-[#1A4D2E] font-medium">View all →</button>
    </div>
  );
}
function NewSectionHeader() {
  return (
    <div className="space-y-4">
      <SectionHeader title="Sales Summary" subtitle="Filtered by today · Main Branch" action={
        <button className="text-[12px] font-medium flex items-center gap-1"
          style={{ color: 'var(--brand-primary)' }}>View all <ChevronRight size={12} /></button>
      } />
      <SectionHeader title="Expense Breakdown" subtitle="This month" />
      <SectionHeader title="Open Purchase Orders" />
    </div>
  );
}

// 10. EmptyState
function OldEmptyState() {
  return (
    <div className="text-center py-12 text-slate-500">
      <p className="text-sm">No sales found for this date range.</p>
      <button className="mt-3 text-sm text-[#1A4D2E] font-medium underline">Make a sale</button>
    </div>
  );
}
function NewEmptyState() {
  return (
    <EmptyState
      icon={Receipt}
      title="No sales found"
      detail="No sales recorded for the selected date range or filters."
      action="New Sale"
    />
  );
}

// 11. DonutChart
function OldDonut() {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm text-slate-600 font-medium">Inventory Health</div>
      <div className="space-y-1.5">
        {[['Healthy','bg-emerald-500',62],['Low Stock','bg-amber-500',18],['Out','bg-red-500',8],['Overstock','bg-blue-500',12]].map(([l,c,v]) => (
          <div key={l} className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-sm ${c}`} />
            <span className="text-xs text-slate-600 flex-1">{l}</span>
            <span className="text-xs font-semibold text-slate-700">{v}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
function NewDonut() {
  return (
    <div className="flex flex-col items-center">
      <p className="text-[11px] font-semibold uppercase tracking-wide mb-3" style={{ color: 'var(--text-secondary)' }}>Inventory Health</p>
      <DonutChart segments={DONUT_INVENTORY} size={120} centerLabel="100" centerSub="Products" />
    </div>
  );
}

// 12. MiniLineChart
function OldLineChart() {
  return (
    <div className="space-y-1">
      <div className="text-sm text-slate-600 font-medium">Sales Trend</div>
      <div className="flex items-end gap-1 h-12">
        {TREND_POINTS.map((p, i) => {
          const max = Math.max(...TREND_POINTS);
          return <div key={i} className="flex-1 bg-emerald-400 rounded-t opacity-70" style={{ height: `${(p/max)*100}%` }} />;
        })}
      </div>
      <div className="flex justify-between text-[10px] text-slate-400">
        <span>Mon</span><span>Sun</span>
      </div>
    </div>
  );
}
function NewLineChart() {
  return (
    <div className="space-y-2 w-full">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>Sales Trend — 8 days</p>
        <span className="text-[11px]" style={{ color: 'var(--num-positive)' }}>+35% growth</span>
      </div>
      <MiniLineChart points={TREND_POINTS} width={280} height={56} />
      <div className="flex justify-between text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
        <span>8 days ago</span><span>Today</span>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SIDEBAR PREVIEW (current vs proposed)
// ══════════════════════════════════════════════════════════════════════════
function SidebarOld() {
  const items = [
    { icon: LayoutDashboard, label: 'Dashboard', active: false },
    { icon: ShoppingCart,    label: 'Sales',     active: true  },
    { icon: Users,           label: 'Customers', active: false },
    { icon: Receipt,         label: 'Expenses',  active: false },
  ];
  return (
    <div className="rounded-lg bg-[#0F172A] p-3 w-full max-w-[220px]">
      <div className="px-1 pb-3 mb-2 border-b border-white/5 flex items-center gap-2">
        <div className="w-6 h-6 rounded-md bg-[#1A4D2E] flex items-center justify-center">
          <Building2 size={12} className="text-white" />
        </div>
        <span className="text-sm font-bold text-white" style={{ fontFamily: 'Manrope' }}>AgriBooks</span>
      </div>
      {items.map(it => (
        <div key={it.label} className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-[13px] font-medium ${
          it.active ? 'bg-[#1A4D2E] text-white' : 'text-slate-400'}`}>
          <it.icon size={16} strokeWidth={1.5} />{it.label}
        </div>
      ))}
    </div>
  );
}
function SidebarNew() {
  const items = [
    { icon: LayoutDashboard, label: 'Dashboard', active: false },
    { icon: ShoppingCart,    label: 'Sales',     active: true  },
    { icon: Users,           label: 'Customers', active: false },
    { icon: Receipt,         label: 'Expenses',  active: false },
  ];
  return (
    <div className="rounded-lg bg-[#0F172A] p-3 w-full max-w-[220px]">
      <div className="px-1 pb-3 mb-2 border-b border-white/5 flex items-center gap-2">
        <div className="w-6 h-6 rounded-md bg-[#1A4D2E] flex items-center justify-center">
          <Building2 size={12} className="text-white" />
        </div>
        <span className="text-sm font-bold text-white" style={{ fontFamily: 'Manrope' }}>AgriBooks</span>
      </div>
      {items.map(it => (
        <div key={it.label} className={`relative flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium transition-colors ${
          it.active ? 'bg-white/[0.06] text-white' : 'text-slate-400 hover:text-white hover:bg-white/[0.04]'}`}
          style={{ transitionDuration: '80ms' }}>
          {it.active && <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] bg-[#22C55E] rounded-r" />}
          <it.icon size={16} strokeWidth={1.75} />{it.label}
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════
export default function UIPreviewPage() {
  return (
    <div className="max-w-7xl mx-auto px-6 pb-20 pt-8" data-testid="ui-preview-page">

      {/* Page header */}
      <div className="mb-10 pb-6 border-b" style={{ borderColor: 'var(--border-default)' }}>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex h-5 items-center px-2 rounded text-[10px] font-semibold uppercase tracking-wide"
                style={{ backgroundColor: 'var(--warning-bg)', color: 'var(--warning-text)' }}>
                Preview Only
              </span>
              <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>/ui-preview</span>
            </div>
            <h1 className="text-[28px] font-bold tracking-tight" style={{ fontFamily: 'Manrope', color: 'var(--text-primary)' }}>
              AgriBooks Design System
            </h1>
            <p className="text-[13px] mt-1.5 max-w-2xl" style={{ color: 'var(--text-secondary)' }}>
              Phase 0 — 12 shared components that every page will inherit.
              Each section shows <strong>Current</strong> (live code) vs <strong>Proposed</strong> (new system).
              Approve each before we touch the production pages.
            </p>
          </div>
          <div className="text-[12px] space-y-1 max-w-xs p-4 rounded-lg"
            style={{ backgroundColor: 'var(--surface-card)', border: '1px solid var(--border-default)' }}>
            <div className="font-semibold text-[11px] uppercase tracking-wide mb-2" style={{ color: 'var(--text-tertiary)' }}>Design DNA</div>
            <div style={{ color: 'var(--text-secondary)' }}><strong style={{ color: 'var(--text-primary)' }}>Font:</strong> Manrope (headings) + IBM Plex Sans (body)</div>
            <div style={{ color: 'var(--text-secondary)' }}><strong style={{ color: 'var(--text-primary)' }}>Numbers:</strong> tabular-nums on IBM Plex (no new font)</div>
            <div style={{ color: 'var(--text-secondary)' }}><strong style={{ color: 'var(--text-primary)' }}>Radius:</strong> 6px inputs · 8px buttons · 12px cards</div>
            <div style={{ color: 'var(--text-secondary)' }}><strong style={{ color: 'var(--text-primary)' }}>Motion:</strong> 80ms fast · 150ms overlay</div>
            <div style={{ color: 'var(--text-secondary)' }}><strong style={{ color: 'var(--text-primary)' }}>Density:</strong> 40px rows · 36px buttons · 20px badges</div>
          </div>
        </div>
      </div>

      {/* ── 1. Num ── */}
      <Row title="1. Num — Financial numeric display"
        note="font-variant-numeric: tabular-nums on IBM Plex Sans. Semantic colors (green = positive, red = negative). Used 1,000+ times across the app.">
        <DemoCard label="Current — proportional, inconsistent colors" tone="current"><OldNum /></DemoCard>
        <DemoCard label="Proposed — tabular, semantic tone props"><NewNum /></DemoCard>
      </Row>

      {/* ── 2. StatusBadge ── */}
      <Row title="2. StatusBadge — Solid-fill status indicator"
        note="Outlined badges replaced with solid fills + uppercase tracking. More readable at small sizes. 10 built-in variants.">
        <DemoCard label="Current — outlined, mixed sizes" tone="current"><OldBadges /></DemoCard>
        <DemoCard label="Proposed — solid fills, uppercase, consistent height"><NewBadges /></DemoCard>
      </Row>

      {/* ── 3. StatCard ── */}
      <Row title="3. StatCard — KPI metric tile"
        note="No drop-shadow. 1px warm border. Tinted icon pill (not full circle). Delta as solid color badge. Label moves to top in 11px uppercase.">
        <DemoCard label="Current — drop shadow, large title, plain delta" tone="current"><OldStatCard /></DemoCard>
        <DemoCard label="Proposed — flat card, tinted icon pill, semantic delta badge"><NewStatCards /></DemoCard>
      </Row>

      {/* ── 4. PillAvatar ── */}
      <Row title="4. PillAvatar — Tinted icon pill for transaction rows"
        note="Round pill replaces plain icon-in-square. Semantic tone (sale=green, expense=amber, PO=blue). Used in Recent Transactions, Cashier lists.">
        <DemoCard label="Current — grey square container, flat icon" tone="current"><OldPillAvatar /></DemoCard>
        <DemoCard label="Proposed — round tinted pill, semantic color per transaction type"><NewPillAvatar /></DemoCard>
      </Row>

      {/* ── 5. AlertRow ── */}
      <Row title="5. AlertRow — Tinted-bg inline alert"
        note="Tinted background + icon container + action link. Four severity levels (success, warning, danger, info). Used for unclosed days, variance, system notices.">
        <DemoCard label="Current — flat border, plain text" tone="current"><OldAlerts /></DemoCard>
        <DemoCard label="Proposed — tinted bg, icon container, severity-colored title"><NewAlerts /></DemoCard>
      </Row>

      {/* ── 6. DataTable ── */}
      <Row title="6. DataTable — Operations-grade data table"
        note="40px rows (was 48px). Warm tinted header (#F1F0ED). Column labels 11px uppercase tracking. Numeric columns auto-apply tabular-nums. Footer slot for totals.">
        <DemoCard label="Current — cold header, 48px rows, inconsistent spacing" tone="current"><OldDataTable /></DemoCard>
        <DemoCard label="Proposed — warm header, 40px rows, tabular-nums, footer total"><NewDataTable /></DemoCard>
      </Row>

      {/* ── 7. ToolbarPills ── */}
      <Row title="7. ToolbarPills — Pill-in-tray tab toggle"
        note="The Sales page pattern standardized system-wide. Left accent bar on active tab. Badge counts for parked/draft items. 80ms transitions.">
        <DemoCard label="Current — Sales page only, heavier pill, xl radius" tone="current"><OldTabs /></DemoCard>
        <DemoCard label="Proposed — reusable, badge counts, left accent bar, 8px radius"><NewTabs /></DemoCard>
      </Row>

      {/* ── 8. PageHeader ── */}
      <Row title="8. PageHeader — Universal page header"
        note="Breadcrumb nav, h1 title, action button slot, and warning bar slot. The unclosed-days banner moves into the warningBar prop — consistent across all pages.">
        <DemoCard label="Current — inline warning div, inconsistent per-page" tone="current"><OldPageHeader /></DemoCard>
        <DemoCard label="Proposed — standardized breadcrumb + warning bar slot"><NewPageHeader /></DemoCard>
      </Row>

      {/* ── 9. SectionHeader ── */}
      <Row title="9. SectionHeader — Section divider"
        note="Consistent h2 + optional subtitle + action slot. 14px Manrope 600. Replaces 20+ hand-rolled section titles across the app.">
        <DemoCard label="Current — ad-hoc per page, inconsistent weight/size" tone="current"><OldSectionHeader /></DemoCard>
        <DemoCard label="Proposed — standardized, subtitle + action slot"><NewSectionHeader /></DemoCard>
      </Row>

      {/* ── 10. EmptyState ── */}
      <Row title="10. EmptyState — Zero-data placeholder"
        note="Text + Lucide icon only. No illustrations. Centered with optional CTA button. Replaces 15+ unique no-data states.">
        <DemoCard label="Current — plain text, no visual hierarchy" tone="current"><OldEmptyState /></DemoCard>
        <DemoCard label="Proposed — icon in neutral pill, Manrope title, tertiary detail"><NewEmptyState /></DemoCard>
      </Row>

      {/* ── 11. DonutChart ── */}
      <Row title="11. DonutChart — Multi-segment donut"
        note="Pure SVG. No new dependency. Used for inventory health, payment mix, AR aging. Center label slot for key metric.">
        <DemoCard label="Current — bar chart, no visual differentiation" tone="current"><OldDonut /></DemoCard>
        <DemoCard label="Proposed — SVG donut, center label, legend"><NewDonut /></DemoCard>
      </Row>

      {/* ── 12. MiniLineChart ── */}
      <Row title="12. MiniLineChart — Sparkline trend chart"
        note="Pure SVG. Soft area fill (8% opacity). Forest green stroke. End dot for current value. Used in stat cards and dashboard sales trends.">
        <DemoCard label="Current — bar chart / no chart" tone="current"><OldLineChart /></DemoCard>
        <DemoCard label="Proposed — SVG line + soft area fill, end dot"><NewLineChart /></DemoCard>
      </Row>

      {/* ── Sidebar reference ── */}
      <Row title="Sidebar reference — Active item treatment"
        note="Solid green block replaced with 2px left accent bar (#22C55E emerald) + 6% white opacity bg. Less heavy, more industrial.">
        <DemoCard label="Current — solid forest-green active block" tone="current">
          <div className="flex items-center justify-center w-full"><SidebarOld /></div>
        </DemoCard>
        <DemoCard label="Proposed — 2px left accent bar + translucent bg">
          <div className="flex items-center justify-center w-full"><SidebarNew /></div>
        </DemoCard>
      </Row>

      {/* Footer */}
      <div className="mt-8 rounded-lg p-5"
        style={{ backgroundColor: 'var(--surface-card)', border: '1px solid var(--border-default)' }}>
        <div className="text-[13px] font-semibold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--text-primary)' }}>
          Phase 0 complete — what comes next
        </div>
        <div className="text-[12px] space-y-1" style={{ color: 'var(--text-secondary)' }}>
          <div>• <strong style={{ color: 'var(--text-primary)' }}>Phase 1:</strong> Layout shell — kill the green top header, replace with white 56px header + compact shift badge</div>
          <div>• <strong style={{ color: 'var(--text-primary)' }}>Phase 2:</strong> Dashboard — KPI tiles, donut chart, mini line charts using these 12 components</div>
          <div>• <strong style={{ color: 'var(--text-primary)' }}>Phase 3+:</strong> Every page follows the scan → recommend → approve → implement cycle</div>
          <div>• <strong style={{ color: 'var(--text-primary)' }}>Zero logic changes</strong> in any phase. All data-testid attributes preserved exactly.</div>
        </div>
      </div>
    </div>
  );
}
